/**
 * Catálogo de microcopy (§13.6). A chave é canônica (`área.elemento.estado`); todo texto visível passa por aqui.
 * Conteúdo = catálogo de §13.6 + tabelas das seções donas (§3.8, §5–§12, §15.3, §16.3.1) + modelos dos eventos (§13.5).
 *
 * Interpolação de t() e tRich():
 * - `{VAR}` troca pela variável (número → fmtInt; texto já formatado pelo chamador com os formatadores de §13.4).
 * - `{palavra:X}` aplica agree() com o gênero em `vars['X.gender']` (palavras de AgreeWord, §13.4.5).
 * - `{VAR|singular|plural}` aplica plural() ao número em `vars.VAR` (singular só com 1).
 * - `{G.gender|masculino|feminino}`: concordância de grupo (agreeGroup, §13.4.5). O chamador passa em `G.gender` o
 *   gênero do grupo (`groupGender()`: 'F' só se todos forem 'F'); `null` usa o masculino.
 * - Variável faltando lança erro em desenvolvimento e vira "–" em produção.
 * - tRich() marca o trecho de `{X}` como candidato quando `vars['X.number']` existe (nomes em `<b>` na cor do partido,
 *   §7.4 e §7.15).
 * - O espaço inseparável entre número e unidade (§13.4.1) vem dos formatadores (fmtVotesLong, fmtVoters, fmtPoints);
 *   t() não troca espaços (decisão da frente copy da Fase 3).
 *
 * Regras de montagem (§13.6, precedência):
 * - Mesma função com chave diferente: vale a chave do catálogo de §13.6 (ex.: `tabs.*`, não `header.tabs.*`;
 *   `share.fallback`, não `share.copyFallback`); o apelido não é criado (lista em copy.duplicates.test.ts).
 * - Texto, variantes ou condição diferentes: valem os da seção dona do componente (regra 2); os textos de §13
 *   (manchete, eventos em todas as formas, anúncios de conexão) valem sobre qualquer outra seção.
 * - Texto citado só na seção dona entra com a chave dela (regra 3). Chaves dos componentes base (§3.8) ficam: não são
 *   apelidos de §5–§12.
 * - Variáveis com os nomes canônicos de §13.6.1 (`{S}`, não `{pct}`; `{URL}`, não `{url}`). Variáveis novas, sem
 *   equivalente em §13.6.1: `{PRODUCT}` (nome do produto), `{KBD}` (atalho), `{TITULO}` (título de slide), `{N1T}`
 *   (UFs definidas no 1º turno), `{faixa}` (§13.5.3) e os relógios `{H:MM:SS_C}`/`{H:MM:SS_P}`.
 * - O nome do produto nunca é escrito à mão: é a variável `{PRODUCT}`, preenchida com VITE_PRODUCT_NAME por quem chama
 *   (§16.1.1). Este arquivo não lê `import.meta.env`: o inlineBootPlugin (scripts/vite/inline-boot.ts) lê a FONTE dele
 *   no Node para os textos do esboço e da guarda (boot.*, brand.*, tabs.*, status.connecting).
 * - Valores repetidos com funções diferentes: a lista de exceções, com o motivo de cada uma, está no próprio
 *   tests/unit/copy.duplicates.test.ts (AC-13.6-03).
 * - Os textos da página do replay (FB-02) ficam em REPLAY_STRINGS, no fim do arquivo, e saem por tReplay(): assim o
 *   pacote do site ao vivo não os carrega (o Rolldown descarta o objeto que ninguém importa; o B01 mede).
 */
import { agree, type Gender, isAgreeWord, plural } from '../format/agree';
import { DASH, fmtInt } from '../format/numbers';

export const STRINGS = {
  // ── Componentes base (§3.8, §3.9) ────────────────────────────────────────────────────────────────────────
  'common.close': 'Fechar',
  'common.dash': DASH,
  'pager.prev': 'Anteriores',
  'pager.next': 'Próximas',
  'pager.label': '{i}/{n}',
  'pager.page.sr': 'Página {i} de {n}',
  'pager.group': 'Páginas',
  'kbd.search.mac': '⌘K',
  'kbd.search.other': 'Ctrl K',
  'kbd.esc': 'Esc',
  'party.tag': '{SIGLA} {NUM}',
  'bar.dual.aria': '{A} {pA}, {B} {pB}; a linha do meio marca 50% dos votos válidos',
  'ring.aria': '{S} das seções',
  'candidateRow.open': 'Ver {NOME}',
  'candidateRow.votes': '{N} {N|voto|votos}',
  // StatusChip (§3.8): rótulos padrão do componente base; os cartões usam chip.* (§13.6.6)
  'chip.status.waiting': 'Aguardando',
  'chip.status.counting': 'Apurando {S}',
  'chip.status.complete': 'Apurado',
  'chip.status.decidedMath': 'Definido',
  'chip.status.elected': '{Eleito:X}',
  'uf.hint.mouse': 'Clique num município',
  'uf.hint.touch': 'Toque num município',
  'uf.hint.keyboard': 'Busque um município ({KBD})',

  // ── Casca do app (§4, §7.1 bloco 8, §15.4.1) ────────────────────────────────────────────────────────────
  // nomes das colunas e do bloco inferior do flow (§7.1 bloco 8, seção dona)
  'panel.left.aria': 'Resumo nacional',
  'panel.right.aria': 'Detalhes',
  // abas da coluna direita com os comentários no ar (proposta do front de 14/10, seção 2.1)
  'panel.tabs.aria': 'O que mostrar na coluna',
  'panel.tab.comments': 'Comentários',
  'panel.tab.updates': 'Atualizações',
  'panel.more.aria': 'Mais sobre a apuração',
  // marcos "Mapa" e "Linha do tempo" (§15.4.1, sem chave em §13.6; `timeline.aria` é o do controle deslizante)
  'shell.map.aria': 'Mapa',
  'shell.timeline.aria': 'Linha do tempo',

  // ── Título da aba (§5.3.4, §13.6.2) ─────────────────────────────────────────────────────────────────────
  'title.view': '{LUGAR} · {CARGO} · {PRODUCT}',
  'title.simPrefix': 'Simulação · ',
  'title.generic': '{PRODUCT} · 2º turno',
  // fase `home` (ORD-F-091): o mesmo título do `<title>`/`og:title` do "vitrine" (ORD-F2-022), já na página de aprovação
  'title.home': 'Apuração do 2º turno 2026 ao vivo, município por município · {PRODUCT}',
  // 26/10: a abertura pós-apuração (o <title> do index.html e da aba na fase `after`). PARA APROVAR
  'title.after': 'O 2º turno de 2026, município por município · {PRODUCT}',
  // formas da tabela de §5.3.4 (regra 3 de §13.6: texto da seção dona sem entrada no catálogo)
  'title.officeOnly': '{CARGO} · {PRODUCT}',
  'title.place.br': 'Brasil',
  'title.place.abroad': 'Exterior',
  'title.place.municipality': '{MUN} ({SIGLA_UF})',
  'title.place.zone': '{ZONA}ª zona · {MUN} ({SIGLA_UF})',
  'title.place.country': '{PAIS} (exterior)',
  'title.place.city': '{CIDADE}, {PAIS}',
  'title.office.president': 'Presidente',
  'title.office.governor': 'Governador',
  'title.office.governors': 'Governadores',
  'title.office.senate': 'Senado (1º turno)',
  'title.office.chamber': 'Deputados (1º turno)',

  // ── Marca e cabeçalho (§6, §13.6.2) ─────────────────────────────────────────────────────────────────────
  // Brand (§6.2)
  'brand.name': '{PRODUCT}',
  'brand.subtitle': '2º turno · 25 de outubro',
  'brand.about': 'Sobre',
  'brand.social.x': '{PRODUCT} no X (abre em nova aba)',
  'brand.social.x.title': '{PRODUCT} no X',
  'brand.social.instagram': '{PRODUCT} no Instagram (abre em nova aba)',
  'brand.social.instagram.title': '{PRODUCT} no Instagram',
  // OfficeTabs (§6.3)
  'tabs.group.aria': 'Cargo',
  'tabs.president': 'Presidente',
  'tabs.governors': 'Governadores',
  'tabs.senate': 'Senado',
  'tabs.chamber': 'Deputados',
  'tabs.counting': ', em apuração',
  'header.tabs.archiveTitle': 'Resultado final do 1º turno · 4 de outubro',
  // SearchButton (§5.8)
  'search.button': 'Buscar',
  'search.button.aria': 'Buscar município, estado, país ou candidato',
  'search.button.ariaZones': 'Buscar município, zona eleitoral, estado, país ou candidato',
  // StatusIndicator (§6.4)
  'status.connecting': 'Conectando aos resultados…',
  // M1 (ORD-F-148): o feed não entregou nenhum número desde a abertura; texto EM APROVAÇÃO (trava feedFailingNotice)
  'status.failing': 'Os números não estão chegando · tentando de novo',
  'status.failing.medium': 'Números não chegam · tentando de novo',
  'status.failing.short': 'Tentando de novo',
  'status.failing.tse': 'Resultados no TSE',
  'status.live': 'Atualizado às {HHhMM} · {S} das seções',
  'status.live.medium': 'Atualizado às {HHhMM} · {S}',
  'status.live.short': '{HHhMM} · {S}',
  'status.replayLoading': 'Carregando {HHhMM}…',
  'status.replay': 'Como estava às {HHhMM} · {S} das seções',
  'status.replay.medium': 'Como estava às {HHhMM} · {S}',
  'status.replay.short': '{HHhMM} · {S}',
  // aviso de coleta em texto neutro (K07; §8 de 14/10, noite): {N} desde o último número do TSE, {V} desde a última
  // verificação do site (M10). PROVISÓRIO até a aprovação dos textos (§8 de 15/10, item 9).
  'status.tseStalled': 'O TSE não envia números novos há {N} · verificado há {V}',
  'status.tseStalled.medium': 'O TSE não envia números novos há {N}',
  'status.tseStalled.short': 'TSE sem números novos · {HHhMM}',
  'status.stalled': 'Sem números novos desde {HHhMM} · {S} das seções',
  'status.stalled.medium': 'Sem números novos desde {HHhMM} · {S}',
  'status.stalled.short': 'Sem números novos · {HHhMM}',
  'status.reconnecting': 'Reconectando · números de {HHhMM} · {S} das seções',
  'status.reconnecting.medium': 'Reconectando · números de {HHhMM} · {S}',
  'status.reconnecting.short': 'Reconectando · {HHhMM}',
  'status.offline': 'Sem internet · números de {HHhMM} · {S} das seções',
  'status.offline.medium': 'Sem internet · números de {HHhMM} · {S}',
  'status.offline.short': 'Sem internet · {HHhMM}',
  'status.agoSeconds': '{N} s',
  'status.agoMinutes': '{N} min',
  'status.title.updated': 'Atualizado às {HHhMM} (horário de Brasília)',
  'status.title.replay': 'Como estava às {HHhMM} (horário de Brasília)',
  // os dois relógios têm valores diferentes: um nome de variável para cada (o catálogo repete {H:MM:SS})
  'status.title.clocks': 'Hora do TSE: {HHhMM} · coletado às {H:MM:SS_C} · publicado às {H:MM:SS_P}',
  'status.title.placesAhead': 'Os estados já somam {S} das seções; o total do TSE chega em seguida.',
  'status.srTimezone': ' (horário de Brasília)',
  // SourceStamp e rodapé de créditos (§6.5)
  'source.stamp': 'Fonte: TSE · dados de {HHhMM}',
  'source.stamp.noData': 'Fonte: TSE',
  'source.stamp.simulation': 'Simulação · dados fictícios',
  'source.stamp.fixture': 'Dados de teste',
  'source.stamp.linkAria': 'TSE, resultados oficiais (abre em nova aba)',
  'source.placesAhead': 'Os estados já somam {S} das seções; o total do TSE chega em seguida.',
  'source.footer.results': 'Resultados: Tribunal Superior Eleitoral (TSE), resultados.tse.jus.br.',
  'source.footer.photos': 'Fotos: TSE / DivulgaCandContas.',
  'source.footer.maps': 'Mapas: IBGE. Demais créditos em Sobre.',
  'source.footer.model': 'Estimativas: modelo próprio; não é pesquisa nem resultado oficial.',
  'source.footer.disclaimer': '{PRODUCT} é um site independente. Os números oficiais são os publicados pelo TSE.',
  // `source.footer.links` de §6.5 lista três textos: uma chave para cada
  'source.footer.links.about': 'Sobre',
  'source.footer.links.method': 'Metodologia',
  'source.footer.links.privacy': 'Privacidade',
  // ExteriorButton (§6.6; as dicas vão no `title`, nunca no nome acessível)
  'exterior.button': 'Exterior',
  'exterior.button.aria.off': 'Ver os votos do exterior',
  'exterior.button.aria.on': 'Voltar ao Brasil',
  // ShareButton (§6.7; ação share() de state/actions.ts; o botão nasce na Fase 11)
  'share.aria': 'Compartilhar esta vista',
  'share.copied': 'Link copiado.',
  'share.fallback': 'Copie o link: {URL}',
  // TvButton (§6.8) e TvExitButton (§12.1)
  'tv.button': 'Tela cheia',
  'tv.button.title': 'Tela cheia, em tamanho de TV',
  'tv.exit': 'Sair da tela cheia',
  // ReplayLink (FB-02, PG-06 a): a entrada do site do replay no cabeçalho, no "Sobre" e no rodapé
  'nav.replay': 'Reveja apurações',
  'nav.replay.aria': 'Reveja apurações anteriores, no replay',
  // SimulationButton e SimulationBanner (§6.9)
  'sim.button': 'Simulação',
  'sim.banner': 'SIMULAÇÃO — dados fictícios',
  // NoticeBar (§6.10, §10.5)
  'notice.noRecord': 'Não há registro de {HHhMM}.',
  // arquivo do TSE ilegível (P-33, K07): texto neutro, sem "falha", "atraso" nem "inconsistência". PROVISÓRIO até a
  // aprovação dos textos (§8 de 15/10, item 9).
  'notice.held': 'Um arquivo do TSE não pôde ser lido agora. Os números {LUGAR} são os da leitura das {HHhMM}.',
  'notice.held.two':
    'Arquivos do TSE não puderam ser lidos agora. Os números {LUGAR} e {OUTRO} são os da última leitura completa.',
  'notice.held.more':
    'Arquivos do TSE não puderam ser lidos agora. Os números {LUGAR} e de outros {N} lugares são os da última leitura completa.',
  // PresenceCounter (§6.11, P2)
  'presence.count': '{N} {N|pessoa|pessoas} agora',
  'presence.aria': '{N} {N|pessoa|pessoas} com a página aberta agora',
  'presence.title': 'Pessoas com a página aberta agora',

  // ── Contagem regressiva e manchete (§10.8, §13.2, §13.6.3) ──────────────────────────────────────────────
  'countdown.days': 'A apuração começa no dia 25, às 17h00',
  'countdown.tomorrow': 'A apuração começa amanhã às 17h00',
  'countdown.today': 'A apuração começa em',
  'countdown.simulation': 'A apuração começa às 17h00',
  'countdown.timer': '{H:MM:SS}',
  // texto sr-only do timer (§10.8): "{N} horas e {M} minutos" · "{M} minutos" · "menos de 1 minuto"
  'countdown.sr': '{N} {N|hora|horas} e {M} {M|minuto|minutos}',
  'countdown.sr.hours': '{N} {N|hora|horas}',
  'countdown.sr.minutes': '{M} {M|minuto|minutos}',
  'countdown.sr.lessThanMinute': 'menos de 1 minuto',
  'headline.connecting': 'Aguardando os resultados',
  'headline.opened': 'A apuração começou às 17h00',
  'headline.abroadFirst': 'Primeiros votos, do exterior',
  'headline.early': 'Ainda é cedo: menos de 1% das seções',
  'headline.leading': '{X} lidera',
  'headline.leading.by': 'por {V}',
  'headline.tie': '{A} e {B} estão empatados',
  'headline.leadChange': '{X} passa à frente às {HHhMM}',
  'headline.above99': 'Projeção: chance de {X} acima de 99%',
  'headline.elected': '{X} é {eleito:X} presidente',
  // ORD-F-149 item 1, em aprovação (proposta; trava em src/copy/editorial.ts): antes do TSE, a manchete e o subtítulo
  // da definição matemática; depois do TSE, a manchete com a fonte. {V} e {E} saem por extenso (fmtInt): na escala
  // curta, "2,3 milhões de votos é maior que os 2,3 milhões de eleitores" pareceria um empate.
  'headline.decided.lead': 'A vantagem de {X} não pode mais ser alcançada',
  'headline.decided.lead.sub':
    'Com {S} das seções, a diferença de {V} votos é maior que os {E} eleitores que ainda faltam apurar.',
  'headline.elected.tse': '{X} é {eleito:X} presidente, segundo o TSE',

  // ── Herói (§7.2–§7.11, §13.6.4) ─────────────────────────────────────────────────────────────────────────
  'hero.kicker.base': 'Presidente · Brasil',
  'hero.kicker.counting': 'Presidente · Brasil · {S} das seções',
  'hero.chip.math': 'Definido matematicamente às {HHhMM}',
  // ORD-F-149 item 1, em aprovação: o selo da definição matemática na proposta e na alternativa
  'hero.chip.math.count': 'Conta dos votos às {HHhMM} · o resultado oficial é do TSE',
  'hero.chip.math.lead': 'Os votos que faltam não mudam a liderança · {HHhMM}',
  'hero.chip.tse': '{Eleito:X} · TSE · {HHhMM}',
  'hero.chip.tse.title': 'Resultado oficial do TSE às {HHhMM}',
  'hero.leader.votes': '{N} {N|voto|votos}',
  'hero.leader.aria': 'Ver o perfil de {NOME}, {SIGLA} {NUM}',
  'hero.leader.share.sr': '{P} dos votos válidos',
  // GoalBar (§7.6): o rodapé é o texto de fmtDistanceTo50; as chaves dão a mesma forma a quem monta
  'goal.fifty': '50%',
  'goal.passed': 'passou de 50%',
  'goal.missing': 'faltam {pontos}',
  'goal.missingOne': 'falta {pontos}',
  'goal.near1': 'a menos de 0,1 ponto',
  'goal.near2': 'a menos de 0,01 ponto',
  'goal.tie': 'em 50%',
  'goal.aria': '{A} {pA}, {B} {pB}; a linha do meio marca 50% dos votos válidos',
  // MarginRows (§7.7)
  'margin.advantage': 'Vantagem',
  'margin.difference': 'Diferença',
  'margin.value': '{pontos} · {V}',
  'margin.empty': DASH,
  'margin.remaining': 'Por apurar',
  'margin.remaining.value': '{E}',
  'margin.none': 'Nenhum',
  // FirstRoundRef (§7.8)
  'firstRound.label': '1º turno',
  'firstRound.item': '{NOME} {P}',
  'firstRound.title': 'O resultado do 1º turno, em 4 de outubro',
  'firstRound.sr': 'Resultado do 1º turno, em 4 de outubro:',
  // StillCountingList (§7.9)
  'stillCounting.title': 'Ainda em apuração',
  'stillCounting.governors': 'Governadores',
  'stillCounting.detail': '{LISTA}',
  'stillCounting.nStates': '{N} estados',
  'stillCounting.aria': 'Governadores, ainda em apuração: {LISTA}. Abre a aba Governadores',
  // HeroKpis (§7.10) e cartões
  'kpi.valid': 'Votos válidos',
  'kpi.turnout': 'Comparecimento',
  'kpi.blankNull': 'Brancos e nulos',
  'kpi.abstention': 'Abstenção',
  // PreResultsGrid (§7.11)
  'pre.candidacies': 'Candidaturas',
  'pre.electorate': 'Eleitorado',

  // ── Por região, curva, projeção e últimas atualizações (§7.12–§7.15, §13.6.5) ───────────────────────────
  'regions.title': 'Por região',
  'regions.aside': 'Quem lidera para presidente',
  'region.N': 'Norte',
  'region.NE': 'Nordeste',
  'region.CO': 'Centro-Oeste',
  'region.SE': 'Sudeste',
  'region.S': 'Sul',
  'region.EX': 'Exterior',
  'regions.row.sub': '{S} · faltam {Q}',
  'regions.row.sub.title': 'Faltam {E}',
  'regions.abroad.closed': 'urnas fechadas em {N} de {M} {M|cidade|cidades}',
  'regions.delta.title': 'Em relação ao {SIGLA} no 2º turno de 2022 ({NOME2022}), em pontos',
  'regions.delta.sr.more': 'mais {pontos} em relação ao {SIGLA} no 2º turno de 2022',
  'regions.delta.sr.less': 'menos {pontos} em relação ao {SIGLA} no 2º turno de 2022',
  'regions.row.aria': '{REGIAO}: {X} {pX}, {S} das seções',
  'regions.abroad.aria.closed': 'Exterior: urnas fechadas em {N} de {M} {M|cidade|cidades}',
  'trend.title': 'Ao longo da apuração',
  'trend.empty': 'A curva começa com 1% das seções',
  'trend.axis.start': '0% das seções',
  'trend.axis.mid': '50%',
  'trend.axis.end': '100%',
  'trend.aria': 'Gráfico dos dois candidatos ao longo da apuração. Com {S} das seções, {A} tem {pA} e {B} tem {pB}.',
  'trend.aria.leadChange': ' A liderança mudou com {P1} das seções.',
  // ProbabilitySlot (§7.14; só com features.probability)
  'prob.title': 'Projeção',
  // início medido pelo especialista (§8 de 15/10, tarde, item 3 D): o mesmo texto do motivo público aceito
  'prob.waiting':
    'A projeção começa com 1% das seções apuradas, com dados de pelo menos 14 unidades da federação e das cinco regiões',
  'prob.chance.label': 'Chance de vencer',
  'prob.chance.value': '{C}%',
  // ORD-F-151 (§8 de 20/10 ~12h13): {N} = {C} × 100 com ponto de milhar (fmtSimWins); os dois nunca discordam, e o teto
  // de 99% sem a chamada aprovada vira 9.900
  'prob.sentence': 'Em 10.000 simulações, {X} vence em {N} ({C}%).',
  'prob.sentence.above99': 'Em 10.000 simulações, {X} vence em mais de 9.900 (mais de 99%).',
  'prob.sentence.even': 'Em 10.000 simulações, cada um vence em 5.000 (50%).',
  // ORD-F-149 item 2, em aprovação. Proposta: só quando o líder da contagem ({X}) não é o favorito da chance; {E} já
  // traz a unidade (fmtVoters). Alternativa: a nota fixa, sempre junto da chance.
  'prob.note.disagree':
    '{X} está à frente nos votos já contados. A chance estima o resultado final e leva em conta os {E} que ainda faltam e onde eles votam.',
  'prob.note.fixed':
    'A contagem é de agora. A chance é uma estimativa do final, que considera os votos que faltam e onde eles estão.',
  // ORD-F-151 B (dois lados, atrás de CHANCE_LAYOUT): o rótulo da metade do apurado, par de "Chance de vencer"
  'prob.twin.counted': 'Votos contados agora · Fonte: TSE',
  'prob.band.openRace': 'Disputa aberta',
  'prob.band.favorite': '{Favorito:X}',
  'prob.band.veryLikely': 'Muito provável',
  'prob.band.above99': 'Chance acima de 99%',
  'prob.final.label': 'Resultado final projetado',
  'prob.final.value': '{NOME} {P}',
  'prob.final.range': 'entre {lo} e {hi}',
  // ProbabilityStrip (chance no topo, C01; faixa de votos final, P-35)
  'prob.strip.votes': 'Resultado final projetado: {A} {rA}; {B} {rB}.',
  'prob.final.aria': 'Resultado final projetado: {A} {pA}, entre {lo} e {hi}; {B} {pB}, entre {P1} e {P2}',
  'prob.time': 'Projeção de {HHhMM}',
  'prob.disclaimer': 'Estimativa estatística feita com os votos já apurados. Não é pesquisa nem resultado oficial.',
  'prob.method': 'Como calculamos →',
  'prob.method.panel': 'Modelo {id}, versão {version}.',
  'prob.method.link': 'Ler a metodologia',
  'prob.paused': 'Projeção pausada',
  'prob.paused.reason': 'Projeção pausada: {motivo}',
  'prob.nav.prev': 'Anterior',
  'prob.nav.next': 'Próximo',
  'prob.nav.dot': 'Mostrar {TITULO}',
  // §7.14 bloco 2 e §15.4.2: nome da seção e papel do carrossel; §7.14 bloco 7: cópia .sr do placar de chance (o rótulo
  // visível "Chance de vencer" é lido antes; a chave não repete "vencer", R5 de §13.7)
  'prob.aria': 'Ao longo da apuração e projeção',
  'prob.roledescription': 'carrossel',
  'prob.chance.sr': '{A} {pA}, {B} {pB}',
  // UpdatesFeed (§7.15); os textos dos itens são os eventos (event.*, §13.5)
  'updates.title': 'Últimas atualizações',
  'updates.empty.beforeOpen': 'Os primeiros números saem às 17h00.',
  // vitrine do replay até 25/10 (§8 de 16/10, 12h20, item 2)
  'updates.replay': 'Reveja a apuração de 2022 →',
  'updates.empty.afterOpen': 'As novidades da apuração aparecem aqui assim que chegam.',
  'updates.more': 'Ver mais',
  'updates.badge.abroad': 'Exterior',
  'updates.badge.more': '+{N}',
  'updates.truncated': 'Os eventos mais antigos da noite não estão disponíveis.',
  'updates.region.aria': 'Lista de atualizações',

  // ── Cartões de lugar e perfil (§8, §13.6.6) ─────────────────────────────────────────────────────────────
  'place.sub': '{S} das seções · {E}',
  'place.close': 'Fechar',
  'place.close.uf': 'Fechar o estado',
  'place.close.municipality': 'Fechar o município',
  'place.close.country': 'Fechar o país',
  'place.close.city': 'Fechar a cidade',
  'place.close.abroad': 'Fechar o exterior',
  'place.close.profile': 'Fechar o perfil',
  'place.prevUf': 'Estado anterior',
  'place.nextUf': 'Próximo estado',
  'place.up': '‹ {LUGAR}',
  'place.up.aria': 'Voltar para {LUGAR}',
  'place.up.aria.abroad': 'Voltar para o exterior',
  'uf.section.president': 'Presidente {uf_em}',
  'uf.noSections': 'Nenhuma seção totalizada ainda.',
  'common.votesFrom17': 'Os votos saem a partir das 17h.',
  'common.noVotes': 'Ainda sem votos apurados.',
  'compare.2022.label': '2022',
  'compare.2022.item': '{NOME2022} {P}',
  'compare.2022.title': 'No 2º turno de 2022',
  'compare.variation.label': 'Variação',
  'compare.variation.value': '{SIGLA_A} {dA} · {SIGLA_B} {dB} pontos',
  // texto da seção dona (§8.2, regra 2): pelo partido, com as fatias de 2022 de cada um ({P1} de A, {P2} de B)
  'compare.variation.title':
    'Pontos percentuais em relação ao 2º turno de 2022 ({SIGLA_A} com {P1}; {SIGLA_B} com {P2})',
  // cópia .sr-only da linha "Variação" (§15.4.3 regra 4: sinais por extenso; o "·" visual vira vírgula, regra 6)
  'compare.variation.sr': '{SIGLA_A} {dA}, {SIGLA_B} {dB} pontos em relação ao 2º turno de 2022',
  'compare.delta.more': 'mais {d}',
  'compare.delta.less': 'menos {d}',
  'uf.section.governor': 'Governador',
  'uf.firstRound.title': '{G.gender|Eleitos|Eleitas} no 1º turno',
  'uf.firstRound.governor': 'Governador',
  'uf.firstRound.senate': 'Senado',
  'uf.noRace.governor': 'Sem 2º turno para governador.',
  'chip.waiting': 'Aguardando',
  'chip.counting': 'Apurando {S}',
  'chip.counted': 'Apurado',
  // ORD-F-152 (alinhado à manchete aprovada, §8 de 20/10): o rótulo curto da definição pela conta, sem a hora aqui
  'chip.math': 'Conta dos votos',
  'chip.tse': '{Eleito:X} · TSE',
  'chip.electedFirstRound': '{Eleito:X} no 1º turno',
  'chip.electedFirstRoundGroup': '{G.gender|Eleitos|Eleitas} no 1º turno',
  'chip.runoff': '2º turno',
  'duel.aria': '{A} {pA}, {B} {pB}; a linha do meio marca 50% dos votos válidos',
  'duel.annulled': 'Votos anulados',
  // MunicipalityCard (§8.4)
  'municipality.section': 'Presidente no município',
  'municipality.sections': '{N} de {M} {M|seção totalizada|seções totalizadas}',
  'municipality.sub': '{SIGLA_UF} · {E}',
  'municipality.loading': 'Carregando o município…',
  'municipality.error': 'Não foi possível carregar o município agora; tentamos de novo em instantes.',
  'municipality.replay': '{S} das seções. Revendo a noite, os votos do município só existem para agora.',
  // §8.4 (dona) separa o rótulo "Governador" (uf.section.governor) da nota (AC-8.4-10)
  'municipality.governorNote': 'O 2º turno é contado por estado.',
  'municipality.seeState': 'Ver o estado {uf_de}',
  // variante governador do cartão municipal (§8.4, P2, features.governor_municipal)
  'mun.governor.title': 'Governador em {MUN}',
  'mun.governor.loading': 'Carregando a cidade…',
  'mun.governor.error': 'Não foi possível carregar a cidade agora; tentamos de novo em instantes.',
  'mun.governor.replay': 'Revendo a noite, os votos da cidade são os de agora.',
  // ZoneList e ZoneSection (§8.4, P2)
  'zones.title': 'Zonas eleitorais',
  'zones.pager': '{i}/{n}',
  'zones.prev': 'Zonas anteriores',
  'zones.next': 'Próximas zonas',
  'zones.row.sub': '{ZONA}ª zona · {E}',
  'zones.row.waiting': 'aguardando',
  'zones.row.loading': 'carregando',
  'zones.note': 'No mapa, a área de cada zona é aproximada, pelos locais de votação.',
  'zone.section': 'Presidente na {ZONA}ª zona',
  'zone.whole': 'Município inteiro',
  'zone.whole.aria': 'Voltar ao município inteiro',
  'zone.note': '{LOCAL} · {S} das seções · {E}',
  // o catálogo repete {P} para dois valores: {P1} = comparecimento, {P2} = brancos e nulos
  'zone.kpis': 'Comparecimento {P1} · Brancos e nulos {P2}',
  // AbroadCard, CountryCard, AbroadCityCard (§8.5)
  'abroad.section': 'Presidente no exterior',
  'abroad.closedCities':
    'As urnas já fecharam em {N} de {M} {M|cidade|cidades}, com {Q1} dos {Q2} eleitores do exterior.',
  'abroad.beforeOpen': 'O TSE divulga os votos a partir das 17h, junto com os do Brasil.',
  'abroad.afterOpen': 'Os votos chegam conforme o TSE totaliza as seções.',
  'abroad.stillVoting': 'Ainda votando: {LISTA}.',
  'abroad.stillVoting.item': '{PAIS} (até {h})',
  'abroad.stillVoting.more': 'mais {N} {N|país|países}',
  'list.and': ' e ',
  'abroad.byCountry': 'Por país',
  'abroad.byCountry.aside': '{N} países',
  'abroad.seeAll': 'Ver todos os {N} países',
  'abroad.seeLess': 'Mostrar menos',
  'abroad.note': 'Fora do Brasil, só se vota para presidente.',
  'country.row.sub': '{N} cidades · {E}',
  'row.status.counted': 'apurado',
  'row.status.counting': 'apurando',
  'row.status.closed': 'urnas fechadas',
  'row.status.votesUntil': 'vota até {h}',
  'row.status.voting': 'votando',
  'country.row.aria': '{PAIS}: {X} {pX}, {S} das seções',
  'country.row.aria.closed': '{PAIS}: urnas fechadas, {S} das seções',
  'country.row.aria.status': '{PAIS}: {STATUS}, {S} das seções',
  'city.row.aria.status': '{CIDADE}: {STATUS}',
  'country.cities': 'Cidades',
  'country.whereVote': 'Onde se vota',
  'country.loading': 'Carregando…',
  // Situação incomum no site ao vivo (DR-33): o índice não veio e não há um anterior bom para mostrar
  'insights.error':
    'Não foi possível carregar as comparações do 1º turno agora. O site tenta de novo a cada atualização.',
  'country.error': 'Não foi possível carregar agora; tentamos de novo em instantes.',
  'country.replay': 'Revendo a noite, os votos por cidade só existem para agora.',
  'city.row.sub': 'fechou às {h} · {E}',
  'city.row.sub.open': 'fecha às {h} · {E}',
  'city.row.aria': '{CIDADE}: {X} {pX}, as urnas fecharam às {h}',
  'city.row.aria.open': '{CIDADE}: {X} {pX}, as urnas fecham às {h}',
  'city.sub': '{PAIS} · {E}',
  'city.note': 'As urnas fecharam às {h}, no horário de Brasília · {N} de {M} {M|seção|seções}',
  'city.note.open': 'As urnas fecham às {h}, no horário de Brasília · {N} de {M} {M|seção|seções}',
  // CandidateProfileCard (§8.6)
  'profile.back': 'Voltar',
  'profile.meta': '{SIGLA} {NUM} · Presidente',
  'profile.meta.uf': '{SIGLA} {NUM} · Presidente · {UF}',
  'profile.meta.governor': '{SIGLA} {NUM} · Governador · {UF}',
  'profile.share': 'dos votos válidos',
  'profile.share.br': 'dos votos válidos no Brasil',
  'profile.share.uf': 'dos votos válidos no estado',
  'profile.share.abroad': 'dos votos válidos no exterior',
  'profile.votes': 'Votos',
  'profile.status': 'Situação',
  'profile.sections': 'Seções apuradas',
  'profile.status.math': 'Conta dos votos',
  'profile.status.tse': '{Eleito:X} · TSE',
  'profile.status.lost': '{nao_eleito:X}',
  'profile.status.leads': 'Lidera',
  'profile.status.second': '2º lugar',
  'profile.status.tie': 'Empate',
  'profile.best': 'Onde vai melhor',
  'profile.worst': 'Onde vai pior',
  'profile.race.governor': 'A disputa pelo governo',
  'profile.seeState': 'Ver o estado',

  // ── Mapa, legenda e tooltip (§9, §13.6.7) ───────────────────────────────────────────────────────────────
  'map.aria.br': 'Mapa do Brasil com quem lidera em cada lugar',
  'map.aria.world': 'Mapa do mundo com as cidades onde brasileiros votam e quem lidera em cada uma',
  // nomes por modo e por UF (§9.1, regra 3)
  'map.aria.br.municipalities': 'Mapa do Brasil por município: quem lidera em cada lugar',
  'map.aria.br.states': 'Mapa do Brasil por estado: quem lidera em cada estado',
  'map.aria.br.advantage': 'Mapa do Brasil com a vantagem do líder, em votos, em cada município',
  'map.aria.br.counted': 'Mapa do Brasil com quanto já foi apurado em cada lugar',
  'map.aria.br.candidate': 'Mapa do Brasil com a parte dos votos válidos de {NOME} em cada lugar',
  'map.aria.uf.municipalities': 'Mapa {uf_de} por município: quem lidera em cada lugar',
  'map.aria.uf.states': 'Mapa {uf_de} por estado: quem lidera em cada estado',
  'map.aria.uf.advantage': 'Mapa {uf_de} com a vantagem do líder, em votos, em cada município',
  'map.aria.uf.counted': 'Mapa {uf_de} com quanto já foi apurado em cada lugar',
  'map.aria.uf.candidate': 'Mapa {uf_de} com a parte dos votos válidos de {NOME} em cada lugar',
  'map.aria.governor': 'Mapa do Brasil com as disputas de governador do 2º turno, por estado',
  'map.aria.senate': 'Mapa do Brasil com os senadores eleitos no 1º turno, por estado',
  'map.aria.chamber': 'Mapa do Brasil com o partido que mais elegeu deputados em cada estado',
  'map.loading': 'Carregando o mapa…',
  // §9.2.7: `br-uf` falhou 3 vezes → OopsCard compacto no retângulo do mapa com o botão
  'map.retry': 'Tentar de novo',
  // Brasil inteiro por município (regra do responsável de 31/10, §8): as malhas das 27 UFs chegam sob demanda; enquanto
  // isso, nenhuma cor de estado finge ser a dos municípios; se não vierem, o mapa diz que mostra os estados
  'map.municipal.loading': 'Carregando os municípios…',
  'map.municipal.loading.aria': 'Mapa do Brasil por município: carregando os municípios',
  'map.municipal.error': 'Os municípios não carregaram. O mapa mostra os estados.',
  // MapTopBar (§9.3)
  'map.modes.aria': 'O mapa mostra',
  'map.mode.municipalities': 'Municípios',
  'map.mode.states': 'Estados',
  'map.mode.advantage': 'Vantagem',
  'map.mode.counted': 'Apurado',
  'map.mode.candidate': 'Candidato',
  'map.mode.color': 'Cor',
  // texto da seção dona (§9.3, regra 2)
  'map.candidate.title': 'O mapa de um candidato: a parte dos votos válidos em cada lugar',
  'map.candidate.disabled': 'O mapa por candidato mostra só os números de agora.',
  'map.picker.aria': 'Candidato no mapa',
  'map.picker.button.aria': 'Candidato no mapa: {NOME}',
  'map.picker.option.aria': '{NOME}, {SIGLA} {NUM}, {P} dos votos válidos no país',
  'map.crumbs.aria': 'Onde você está',
  'map.crumbs.br': 'Brasil',
  'map.crumbs.abroad': 'Exterior',
  // MapLegend (§9.6); a unidade concorda com o número ao lado
  'legend.count': '{SIGLA} {N}',
  'map.legend.unit.municipalities': '{N|município|municípios}',
  'map.legend.unit.states': '{N|estado|estados}',
  'map.legend.unit.cities': '{N|cidade|cidades}',
  'map.legend.unit.zones': '{N|zona|zonas}',
  'map.legend.unit.seats': '{N|vaga|vagas}',
  'legend.others': 'Outros {N}',
  'legend.ramp.margin': 'até 10 · 25 · 45 · mais pontos',
  'map.legend.margin.title': 'Vantagem do líder, em pontos',
  'map.legend.margin.aria': 'Tons pela vantagem do líder: até 10, 25, 45 e mais pontos',
  'legend.counted': '{N} de {M} {M|município apurado|municípios apurados}',
  'legend.counted.cities': '{N} de {M} {M|cidade apurada|cidades apuradas}',
  'legend.counted.zones': '{N} de {M} {M|zona apurada|zonas apuradas}',
  'legend.counted.states': '{N} de {M} {M|estado apurado|estados apurados}',
  'legend.ramp.counted.lo': 'menos de 25%',
  'legend.ramp.counted.lo.narrow': '< 25%',
  'legend.ramp.counted.hi': '100%',
  // item 1.10 (ORD-F-078/082), PARA APROVAR
  'map.legend.early': 'Ainda é cedo: as cores mudam muito no começo',
  'map.legend.counted.title': 'Quanto das seções de cada lugar já foi apurado',
  'legend.advantage.party': '{SIGLA} +{Q}',
  'legend.advantage.key': '10 mil · 100 mil · 500 mil',
  'map.legend.advantage.text': 'votos de vantagem',
  'map.legend.advantage.title': 'Altura de cada pico: a vantagem do líder no município, em votos',
  'legend.candidate.ahead': 'À frente em {N} {N|município|municípios}',
  'legend.candidate.ahead.zones': 'À frente em {N} {N|zona|zonas}',
  'legend.candidate.ahead.cities': 'À frente em {N} {N|cidade|cidades}',
  'legend.candidate.ahead.states': 'À frente em {N} {N|estado|estados}',
  'legend.candidate.strongest': 'Mais forte em {LUGAR} · {P}',
  'legend.candidate.country': 'No país · {P}',
  'legend.candidate.ramp.lo': 'menos de {P1}',
  'legend.candidate.ramp.hi': '{P2} ou mais',
  'legend.candidate.title':
    'A parte dos votos válidos que {NOME} tem em cada lugar. No país: {P}. Os tons mudam em {LISTA}.',
  'legend.governors.ramp': 'claro: apurando · forte: definido',
  'map.legend.governor.title': 'O tom diz o andamento da disputa',
  'legend.abroad.before': 'urnas fechadas {N} · votando {M} cidades',
  'legend.loading': 'Carregando…',
  'legend.waiting': 'Aguardando os votos',
  // UfMarkers, SideChips, ExteriorChip (§9.7); texto da seção dona (regra 2), ordem 13 | 22
  'map.marks.aria': 'Estados no mapa',
  'marker.aria': '{UF}: {A} {pA}, {B} {pB}, {S} das seções',
  'marker.aria.counted': '{UF}, {S} das seções apuradas',
  'marker.aria.noData': '{UF}, sem dados',
  'marker.aria.noRace': '{UF}, sem disputa neste turno',
  'marker.aria.waiting': '{UF}, aguardando',
  'map.marker.aria.candidate': '{UF}, {NOME} {P}',
  'map.marker.aria.governor': '{UF}, governador: {A} {pA}, {B} {pB}',
  'map.marker.aria.governor.elected': '{UF}, governador: {A} {pA}, {B} {pB}, {X} {eleito:X}',
  // ORD-F-150 (item 1; PERGUNTA-F-10): no leitor de tela do mapa, nada de "eleito" antes do TSE; depois, "segundo o TSE"
  'map.marker.aria.governor.count':
    '{UF}, governador: {A} {pA}, {B} {pB}, a vantagem de {X} não pode mais ser alcançada',
  'map.marker.aria.governor.elected.tse': '{UF}, governador: {A} {pA}, {B} {pB}, {X} {eleito:X}, segundo o TSE',
  'map.marker.aria.deputies': '{UF}, partido com mais eleitos: {SIGLA}',
  'map.exteriorChip.aria': 'Exterior: {A} {pA}, {B} {pB}',
  'map.exteriorChip.aria.noData': 'Exterior, sem dados',
  'map.exteriorChip.aria.counted': 'Exterior, {S} das seções apuradas',
  // ZoomControls (§9.8)
  'zoom.group.aria': 'Zoom do mapa',
  'zoom.in': 'Aproximar o mapa',
  'zoom.out': 'Afastar o mapa',
  'zoom.reset': 'Voltar ao enquadramento',
  'zoom.in.title': 'Aproximar (+)',
  'zoom.out.title': 'Afastar (−)',
  'zoom.reset.title': 'Voltar ao enquadramento (0)',
  // MapTooltip (§9.10)
  'tooltip.sections': '{S} das seções',
  'tooltip.sections.city': '{PAIS} · {S} das seções',
  'tooltip.waitingFirst': 'Aguardando os primeiros votos.',
  'tooltip.electedFirstRound': '{NOME} foi {eleito:NOME} no 1º turno.',
  'tooltip.waitingRunoff': 'Aguardando os dados do 2º turno.',
  'tooltip.zone': 'Zona {ZONA}',
  'tooltip.zone.sub': '{ZONA}ª zona · {S} das seções',
  'map.tooltip.delta.unit': 'pontos',
  'map.tooltip.delta.title': 'Pontos percentuais em relação ao 2º turno de 2022',

  // ── Linha do tempo, toast e TV (§10, §12, §13.6.8) ──────────────────────────────────────────────────────
  'timeline.aria': 'Hora da apuração',
  'timeline.valuetext.live': '{HHhMM}, ao vivo',
  'timeline.valuetext.replay': '{HHhMM}',
  'timeline.live': 'Ao vivo',
  'timeline.goLive': 'Voltar ao vivo',
  // 26/10 (DECISOES §86): na fase `after` a apuração acabou; a linha do tempo não diz "ao vivo" nem mostra o relógio
  'timeline.valuetext.closed': '{HHhMM}, apuração encerrada',
  'timeline.closed': 'Encerrada',
  'timeline.goEnd': 'Voltar ao fim',
  'timeline.readonly': 'Linha do tempo da apuração, ao vivo',
  // pinos (§10.3): forma `pin` dos eventos de saliência 3 (§13.5.1)
  'pin.leadChange': 'Mudança de liderança às {HHhMM}',
  // ORD-F-152: o rótulo curto da definição pela conta (só com a trava do item 1 em 'today'; a aprovada usa pin.math.count)
  'pin.math': 'Conta dos votos às {HHhMM}',
  // ORD-F-150 (resposta à PERGUNTA-F-10, 20/10 08:51): o selo do mapa com a proposta do item 1, sem a hora
  'pin.math.count': 'Vantagem não pode mais ser alcançada',
  'pin.tse': 'Resultado oficial do TSE às {HHhMM}',
  // EventToast (§10.7): texto = forma `short` do evento + " · {HHhMM}"
  'toast.close.aria': 'Fechar o aviso',
  'toast.text': '{q} · {HHhMM}',
  'tv.ticker.label': 'Últimas',
  'tv.scene.firstVotes': 'Primeiros votos · {HHhMM}',
  'tv.scene.townComplete': 'Apuração concluída · {HHhMM}',
  'tv.scene.abroadFirst': 'Primeiros votos do exterior · {HHhMM}',
  'tv.scene.abroadComplete': 'Exterior apurado · {HHhMM}',
  'tv.scene.cityAlmost': 'Quase toda apurada · {HHhMM}',
  // cena `race` (§12.2): kicker de §13.5.2
  'tv.scene.governor.math': 'Governador · definido às {HHhMM}',
  'tv.scene.governor.tse': 'Governador · resultado oficial às {HHhMM}',

  // ── Governadores, arquivo do 1º turno e busca (§5.8, §11, §13.6.9) ──────────────────────────────────────
  'gov.title': 'Governadores',
  'gov.aside': '2º turno em {N} {N|estado|estados}',
  // contagem (§11.1, dona): "definidos" = fase decided; "em apuração" = counting e no_data
  'gov.tally.elected': '{N} {N|definido|definidos}',
  'gov.tally.counting': '{N} em apuração',
  'gov.tally.none': 'Nenhum resultado definido ainda',
  'gov.tally.firstRound': '{N} {N|definido|definidos} no 1º turno',
  'gov.tally.sr':
    '{N} {N|disputa definida|disputas definidas} e {M} em apuração; {N1T} {N1T|estado definiu|estados definiram} o governador no 1º turno',
  'gov.strip.aria': '{UF}, {X} {pX}',
  // UfStrip (§11.1 item 7, dona): definida pelo TSE e só pela definição matemática
  'gov.strip.aria.elected': '{UF}, {X} {eleito:X}, {pX}',
  'gov.strip.aria.decided': '{UF}, {X} com a eleição definida, {pX}',
  'gov.strip.aria.waiting': '{UF}, aguardando',
  'governors.strip.label': 'Estados com 2º turno para governador',
  'gov.list.title': 'Disputas, das mais apertadas',
  'gov.pager.prev': 'Anteriores',
  'gov.pager.next': 'Próximas',
  'gov.row.elected': '{eleito:X}',
  'governors.row.decided': 'definida',
  'gov.row.waiting': 'Aguardando as primeiras seções',
  // RaceRow (§11.1 item 7, dona): ordem 13 | 22 e % das seções
  'gov.row.aria': '{UF}: {A} {pA}, {B} {pB}, {S} das seções',
  'gov.row.aria.elected': '{UF}: {A} {pA}, {B} {pB}, {S} das seções, {eleito:X}',
  'gov.row.aria.decided': '{UF}: {A} {pA}, {B} {pB}, {S} das seções, eleição definida',
  'gov.row.aria.waiting': '{UF}: aguardando as primeiras seções, 2 candidaturas',
  // arquivo do 1º turno (§11.5–§11.8, P2)
  'archive.label': 'Resultado final do 1º turno · 4 de outubro',
  'archive.loading': 'Carregando o resultado do 1º turno…',
  'archive.senate.title': 'Senado',
  'archive.senate.aside': 'Duas vagas por estado',
  'archive.senate.staying': 'No cargo até 2031: {NOME} ({SIGLA})',
  'senate.tally': '{N} com as duas vagas',
  'senate.list.title': 'A disputa pela 2ª vaga',
  'ideology.left': 'Esquerda',
  'ideology.center': 'Centro',
  'ideology.right': 'Direita',
  'ideology.majority': 'maioria {M} · Centro: PP, União Brasil, PSD, Republicanos e MDB',
  'ideology.how': 'Como classificamos',
  'archive.chamber.title': 'Câmara dos Deputados',
  'archive.chamber.aside': '513 vagas',
  'chamber.state.title': 'Assembleias Legislativas',
  'chamber.state.aside': '1.059 vagas',
  'chamber.state.total': 'eleitos de 1.059 nas 27 assembleias',
  'chamber.topState': 'Mais votados para deputado estadual',
  'chamber.hint.click': 'ou clique num estado no mapa para ver só os dele',
  'chamber.hint.tap': 'ou toque num estado no mapa para ver só os dele',
  'chamber.house.label': 'Deputados',
  'archive.house.federal': 'Federais',
  'archive.house.state': 'Estaduais',
  'archive.topVoted': 'Mais votados do Brasil',
  'archive.seeAll': 'Ver todos os {N} candidatos',
  'archive.back': 'Voltar',
  'archive.filter.placeholder': 'Nome, número ou UF',
  'archive.filter.empty': 'Ninguém com “{q}” para deputado federal {uf_em}.',
  'archive.filter.empty.state': 'Ninguém com “{q}” para deputado estadual {uf_em}.',
  // CandidateListPanel (§11.8, P2)
  'candList.title': '{CARGO} · {LUGAR} · {N} {N|candidato|candidatos}',
  'candList.only10': 'Só os 10',
  'candList.more': 'Mostrar mais 100 · faltam {N}',
  'candList.loading': 'Carregando a lista de candidatos…',
  'candList.empty': 'Ninguém com “{q}”.',
  'candList.error': 'Não foi possível carregar a lista agora.',
  'candList.retry': 'Tentar de novo',
  'candList.filter.placeholderUf': 'Nome ou número',
  // SearchPalette (§5.8)
  'search.kbd.mac': '⌘K',
  'search.kbd.other': 'Ctrl K',
  'search.kbd.esc': 'Esc',
  'search.dialog.aria': 'Buscar',
  'search.input.aria': 'Buscar município, estado, país ou candidato',
  'search.input.ariaZones': 'Buscar município, zona eleitoral, estado, país ou candidato',
  'search.input.placeholder': 'Município, estado, país ou candidato',
  'search.input.placeholder.zones': 'Município, zona, estado, país ou candidato',
  'search.input.placeholder.archive': 'Município, estado, país, candidato ou deputado',
  'search.input.placeholder.zonesArchive': 'Município, zona, estado, país, candidato ou deputado',
  'search.results.aria': 'Resultados da busca',
  'search.group.largest': 'Maiores cidades',
  'search.group.largestUf': 'Maiores cidades {uf_de}',
  'search.group.largestAbroad': 'Maiores cidades no exterior',
  'search.sub.uf': 'Estado',
  'search.sub.df': 'Unidade da Federação',
  'search.sub.municipality': 'Município · {E}',
  'search.sub.abroad': '{N} cidades em {M} países · só presidente',
  'search.sub.country': 'Exterior · {N} cidades · {E}',
  'search.sub.countryOne': 'Exterior · {E}',
  'search.sub.city': 'Exterior · {PAIS} · {E}',
  'search.sub.president': 'Presidente · {SIGLA} {NUM}',
  'search.sub.governor': 'Governo {uf_de} · {SIGLA} {NUM}',
  'search.sub.zone': '{ZONA}ª zona · {MUN}, {SIGLA_UF} · {E}',
  'search.sub.deputy': '{CARGO} · {SIGLA} · {SIGLA_UF} · {N} {N|voto|votos}',
  'search.sub.deputyElected': ' · {eleito:NOME}',
  'search.empty': 'Nada encontrado para “{q}”.',
  'search.loading': 'Carregando a lista de lugares…',
  'search.loading.deputies': 'Carregando a lista de deputados…',
  'search.footer': '↑ ↓ navegar · ↵ abrir · Esc fechar',
  'search.cancel': 'Cancelar',
  // status da busca, anunciado pela LiveRegion (§15.3, X3)
  'search.status.count': '{N} resultados',
  'search.status.one': '1 resultado',
  'search.status.empty': 'Nada encontrado para “{q}”.',

  // ── Parceiro, boot e erros (§12.5, §12.7, §12.8.1, §13.6.10) ────────────────────────────────────────────
  // PartnerSlot (§12.5; desligado por padrão; no dia 25 decide a chave, ORD-F-134). Rótulo "Publicidade" (D2 do
  // responsável, ORD-F-133); textos novos só pela página de aprovação.
  'partner.label': 'Publicidade',
  'partner.label.partner': 'Parceiro',
  'partner.more': 'Saber mais →',
  // §12.5.7: a faixa é um único link
  'partner.bar.aria': '{label}: {sponsor}. {text}Saber mais (abre em nova aba)',
  'partner.close': 'Fechar o anúncio',
  'partner.dot': 'Peça {i} de {n}',
  'partner.region.ad': 'Anúncios',
  'partner.region.partner': 'Parceiros',
  // BootGuard (= §13.6); chegam à guarda inline pelo inlineBootPlugin
  'boot.unsupported':
    'Este navegador não consegue exibir a apuração. Atualize-o ou acompanhe os resultados no site do TSE.',
  'boot.failed': 'Não foi possível abrir a página neste navegador.',
  'boot.slow': 'A página está demorando para abrir. Confira a conexão e tente de novo.',
  'boot.retry': 'Tentar de novo',
  'boot.tse': 'Resultados no site do TSE',
  'noscript.text':
    'Este site precisa de JavaScript para mostrar a apuração. Os resultados oficiais estão em resultados.tse.jus.br.',
  // OopsCard e RootErrorBoundary
  'error.oops': 'Esta parte da tela teve um problema e volta em instantes.',
  'error.root': 'A tela teve um problema e volta em instantes.',
  'error.reload': 'Recarregar agora',

  // ── LiveRegion: anúncios de conexão (§13.5.5, §15.3) ────────────────────────────────────────────────────
  'live.offline': 'Sem conexão. Mostrando números de {HHhMM}.',
  'live.stalled': 'Sem números novos desde {HHhMM}.',
  'live.online': 'Conexão retomada. Números de {HHhMM}.',

  // ── Sobre e créditos (§12.6, §15.2.7, §16.3.1) ──────────────────────────────────────────────────────────
  // variante live ("Sobre"); título e links de §13.6.10
  'about.title': 'Sobre o {PRODUCT}',
  'about.live.lead':
    'Acompanhamento independente da apuração do 2º turno de 25 de outubro de 2026, com números do Tribunal Superior Eleitoral (TSE) consultados a cada 15 segundos.',
  'about.live.official': 'O resultado oficial está em {LINK}.',
  'about.live.officialLink': 'resultados.tse.jus.br',
  // uma chave só para as duas variantes (sem valor repetido)
  'about.timezone': 'Horários de Brasília.',
  'about.link.method': 'Como funciona',
  'about.link.privacy': 'Privacidade',
  'about.link.team': 'Quem faz',
  // Expediente confirmado pelo responsável em 09/10/2026 (§16.7.5, DR-16; docs/DECISOES.md §7)
  'about.expediente':
    'Responsável: Roberto Biagioni · Biamar - Fiscal Social, CNPJ 48.330.555/0001-28 · contato@fiscal.social',
  // variante simulation ("Simulação")
  'about.sim.title': 'Simulação',
  'about.sim.lead':
    'Os números desta tela são fictícios, gerados para testar a página. Não são pesquisa, previsão nem resultado.',
  'about.sim.names.public': 'Os nomes dos candidatos também são fictícios.',
  'about.sim.names.local': 'Os candidatos e as fotos são os do cadastro do TSE; os votos são inventados.',
  'about.sim.pace': 'O ritmo da apuração imita o de eleições anteriores, estado por estado.',
  'about.sim.compare': 'As comparações com 2022 usam o resultado real do 2º turno de 2022.',
  // chave X1 (§15.2.7)
  'about.shortcuts.title': 'Atalhos de teclado',
  'about.shortcuts.single': 'Atalhos de uma tecla',
  // créditos (§16.3.1): sem fotos, o AboutDialog tira a frase das fotos; com probabilidade, acrescenta o modelo
  'credits.footer':
    'Resultados: Tribunal Superior Eleitoral (resultados.tse.jus.br), dados abertos. Malha territorial: IBGE. Mapa-múndi: Natural Earth. Fotos: TSE / DivulgaCandContas.',
  'credits.footer.geonames': ' Coordenadas das cidades do exterior: GeoNames (CC BY 4.0).',
  'credits.footer.model': ' Estimativas: modelo próprio, não é pesquisa nem resultado oficial.',

  // ── Eventos (§13.5; dono único dos textos em todas as formas; montados por src/copy/events.ts) ──────────
  // {X}/{Y} = líder e segundo no lugar do evento; {A}/{B} = ordem 13 | 22 (empates); {UF} = placeName
  // §13.5.1 Presidente
  'event.resultsOpen.line': 'O TSE começa a divulgar os resultados.',
  'event.abroadFirstVotes.line.one': 'Exterior: {C1} traz os primeiros votos da noite.',
  'event.abroadFirstVotes.line.many': 'Exterior: {LISTA} trazem os primeiros votos da noite.',
  'event.abroadFirstVotes.line.none': 'Exterior: os primeiros votos da noite vêm de fora do país.',
  'event.abroadHalf.line': 'Exterior: com metade das seções, {X} lidera fora do país, com {pX}.',
  'event.abroadHalf.line.tie': 'Exterior: com metade das seções, empate entre {A} e {B}.',
  'event.abroadComplete.line': 'Exterior apurado: {X} tem {pX} dos votos válidos fora do país.',
  'event.abroadComplete.line.tie': 'Exterior apurado: empate entre {A} e {B}.',
  'event.ufFirstVotes.line.list': 'Primeiros votos para presidente {LISTA}.',
  'event.ufFirstVotes.line.many': 'Primeiros votos para presidente em {N} estados.',
  'event.ufComplete.line': '{UF}: todas as seções totalizadas, {X} com {pX} e {Y} com {pY}.',
  'event.ufComplete.line.tie': '{UF}: todas as seções totalizadas, empate entre {A} e {B}.',
  'event.capitalComplete.line': '{UF}: {CAP} apurada, {X} vence na capital por {pontos}.',
  'event.capitalComplete.line.sameName': '{UF}: capital apurada, {X} vence por {pontos}.',
  'event.capitalComplete.line.tie': '{UF}: {CAP} apurada, com empate entre {A} e {B}.',
  'event.capitalComplete.line.sameNameTie': '{UF}: capital apurada, com empate entre {A} e {B}.',
  'event.sectionsBatch.line': '+{N} {N|seção|seções}',
  'event.sectionsBatch.line2': '{X} {pX} · {Y} {pY}',
  'event.sectionsBatch.ticker': '+{N} {N|seção|seções}: {SIGLAS} · {X} {pX} · {Y} {pY}',
  'event.sectionsBatch.ticker.noPlaces': '+{N} {N|seção|seções} · {X} {pX} · {Y} {pY}',
  'event.sectionsBatch.sr': 'Mais {N} {N|seção totalizada|seções totalizadas}, em {SIGLAS}. {X} {pX}, {Y} {pY}.',
  'event.sectionsBatch.sr.noPlaces': 'Mais {N} {N|seção totalizada|seções totalizadas}. {X} {pX}, {Y} {pY}.',
  'event.countMilestone.line': '{M}% das seções totalizadas: {X} {pX}, {Y} {pY}.',
  'event.nationalLeadChange.line': '{X} passa à frente de {Y}, com {S} das seções totalizadas.',
  'event.nationalLeadChange.short': '{X} passa à frente de {Y}',
  'event.nationalLeadChange.overlayKicker': 'Presidente · Mudança de liderança · {HHhMM}',
  'event.nationalLeadChange.overlaySub':
    'Com {S} das seções totalizadas, {X} tem {V} a mais. Falta apurar mais {reg_em}, onde {Z} lidera.',
  'event.nationalLeadChange.overlaySub.same':
    'Com {S} das seções totalizadas, {X} tem {V} a mais. Falta apurar mais {reg_em}, onde {X} também lidera.',
  'event.nationalLeadChange.overlaySub.noRegion': 'Com {S} das seções totalizadas, {X} tem {V} a mais.',
  'event.ufLeadChange.line': '{UF}: {X} passa à frente de {Y} para presidente.',
  'event.mathDecision.line': 'Definido matematicamente: {X} é {eleito:X} presidente.',
  'event.mathDecision.short': '{X} é {eleito:X} presidente',
  // ORD-F-150 (item 1 aprovado, §8 de 20/10): fora do herói, o mesmo texto; nada de "é eleito" antes do TSE (PERGUNTA-F-10)
  'event.mathDecision.line.count': 'A vantagem de {X} não pode mais ser alcançada; o resultado oficial é do TSE.',
  'event.mathDecision.short.count': 'A vantagem de {X} não pode mais ser alcançada',
  'event.mathDecision.overlayKicker': 'Presidente · Conta dos votos · {HHhMM}',
  'event.mathDecision.overlaySub': 'Com {S} das seções totalizadas, os votos que faltam não mudam o resultado.',
  'event.tseResult.line': 'Resultado oficial do TSE: {X} é {eleito:X} presidente.',
  'event.tseResult.short': '{X} é {eleito:X} presidente · TSE',
  // ORD-F-150 (resposta à PERGUNTA-F-10, 20/10 08:51): depois do TSE, "…, segundo o TSE", como a manchete do herói
  'event.tseResult.line.count': '{X} é {eleito:X} presidente, segundo o TSE.',
  'event.tseResult.short.count': '{X} é {eleito:X} presidente, segundo o TSE',
  'event.tseResult.overlayKicker': 'Presidente · Resultado oficial do TSE · {HHhMM}',
  'event.tseResult.overlaySub': '{X} tem {pX} dos votos válidos, com {S} das seções totalizadas.',
  // §13.5.2 Governador (escopo UF)
  'event.governorMathDecision.line': '{UF}: definido matematicamente, {X} é {eleito_governador:X}.',
  'event.governorMathDecision.line.count':
    '{UF}: a vantagem de {X} não pode mais ser alcançada; o resultado oficial é do TSE.',
  'event.governorTseResult.line': '{UF}: resultado oficial do TSE, {X} é {eleito_governador:X}.',
  'event.governorTseResult.line.count': '{UF}: {X} é {eleito_governador:X}, segundo o TSE.',
  // §13.5.3 Projeção (nature = 'model'; só com features.probability). {faixa} = event.modelBand.*
  'event.modelBand.openRace': 'disputa aberta',
  'event.modelBand.favorite': '{X} é {favorito:X}',
  'event.modelBand.veryLikely': 'vitória de {X} muito provável',
  'event.modelBand.above99': 'chance de {X} acima de 99%',
  'event.modelActivated.line': 'Projeção ativa às {HHhMM}: {faixa}.',
  'event.modelBandChange.line': 'Projeção revisada às {HHhMM}: {faixa}.',
  'event.modelPaused.line': 'Projeção pausada às {HHhMM}.',
  'event.modelPaused.line.reason': 'Projeção pausada às {HHhMM}: {motivo}.',
  'event.modelResumed.line': 'Projeção retomada às {HHhMM}.',
  // §13.7 R2/R5 vencem §13.5.3 (regra 4): sem "o resultado está definido matematicamente" num texto de estimativa
  'event.modelEnded.line': 'Projeção encerrada às {HHhMM}.',
  // motivos públicos dos códigos de MODEL_EVENT_REASONS (P-04)
  'event.modelReason.sourceStalled': 'aguardando novos números do TSE',
  'event.modelReason.modelHealth': 'modelo em revisão',
  'event.modelReason.editorial': 'decisão editorial',
  // §13.5.4 Operação (nature = 'ops')
  'event.tseStalled.line': 'TSE sem novidade desde {HHhMM}.',
  'event.tseResumed.line': 'O TSE voltou a publicar às {HHhMM}.',
  'event.correction.line': '{UF}: o TSE corrigiu {campo}, de {antes} para {depois}.',
  'event.correction.field.sectionsCounted': 'as seções totalizadas',
  'event.correction.field.validVotes': 'os votos válidos',
  'event.correction.field.votes': 'os votos de {NOME}',
  // FRONT-2 (ORD-F2-022/023, item 1.1 e D12): menu e rodapé, atrás de VITE_POLLS_URL e VITE_CHANNEL_URL vazias; PARA APROVAR
  'nav.polls': 'Pesquisas',
  'nav.polls.aria': 'Pesquisas registradas no TSE',
  'source.footer.links.channel': 'Siga o canal',
} as const satisfies Record<string, string>;

export type CopyKey = keyof typeof STRINGS;
export type CopyVars = Readonly<Record<string, string | number | Gender | null | undefined>>;

/** Trecho de texto rico (§13.2.1): `candidate` marca o nome de um candidato (renderizado em `<b>` na cor dele). */
export interface RichPart {
  text: string;
  candidate?: string;
}
export type Rich = RichPart[];

/** Gênero de um grupo para `{G.gender|…|…}` (§13.4.5): feminino só se TODOS forem 'F'. */
export function groupGender(gs: readonly (Gender | null | undefined)[]): Gender {
  return gs.length > 0 && gs.every((g) => g === 'F') ? 'F' : 'M';
}

function missing(key: string, name: string): string {
  if (import.meta.env.DEV) throw new Error(`[copy] ${key}: variável {${name}} ausente`);
  return DASH;
}

const TOKEN = /\{([^{}]+)\}/g;

/** Preenche os marcadores `{…}` de um modelo (a mesma regra de t(); usada pelo catálogo da análise, copy/analysis.ts). */
export function interpolate(key: string, template: string, vars: CopyVars = {}): string {
  return template.replace(TOKEN, (_, body: string) => resolve(key, body, vars));
}

/** Valor de um marcador `{…}` do modelo (sem o trecho de candidato). */
function resolve(key: string, body: string, vars: CopyVars): string {
  const [name, ...rest] = body.split('|') as [string, ...string[]];
  if (rest.length === 2) {
    const [a, b] = rest as [string, string];
    if (name.endsWith('.gender')) {
      const g = vars[name];
      if (g === undefined) return missing(key, name);
      return g === 'F' ? b : a;
    }
    const n = vars[name];
    if (typeof n !== 'number') return missing(key, name);
    return plural(n, a, b);
  }
  const colon = body.indexOf(':');
  if (colon > 0) {
    const word = body.slice(0, colon);
    const who = body.slice(colon + 1);
    if (isAgreeWord(word)) return agree(vars[`${who}.gender`] as Gender | null | undefined, word);
  }
  const v = vars[body];
  if (v === undefined || v === null) return missing(key, body);
  return typeof v === 'number' ? fmtInt(v) : v;
}

/** Texto rico da chave: as variáveis com `vars['{nome}.number']` viram trechos de candidato (§13.6). */
export function tRich(key: CopyKey, vars: CopyVars = {}): Rich {
  const tpl: string = STRINGS[key];
  const out: Rich = [];
  const push = (text: string, candidate?: string) => {
    if (text === '') return;
    const last = out[out.length - 1];
    if (candidate === undefined && last && last.candidate === undefined) last.text += text;
    else out.push(candidate === undefined ? { text } : { text, candidate });
  };
  let at = 0;
  for (const m of tpl.matchAll(TOKEN)) {
    const idx = m.index ?? 0;
    const body = m[1] as string;
    push(tpl.slice(at, idx));
    const number = vars[`${body}.number`];
    push(resolve(key, body, vars), typeof number === 'string' ? number : undefined);
    at = idx + m[0].length;
  }
  push(tpl.slice(at));
  return out;
}

/** Texto da chave com as variáveis interpoladas (§13.6). */
export function t(key: CopyKey, vars: CopyVars = {}): string {
  return (STRINGS[key] as string).replace(TOKEN, (_, body: string) => resolve(key, body, vars));
}

/** Texto puro de um `Rich`. */
export function richText(rich: Rich): string {
  return rich.map((p) => p.text).join('');
}

// ── Página do replay (FB-02) ─────────────────────────────────────────────────────────────────────────────────
/**
 * Textos que só a página do replay mostra (replay.html, src/replay/). Separados de STRINGS para o site ao vivo não os
 * carregar: quem os lê é tReplay(), que só src/replay/ importa (tests/unit/replay/copy.test.ts confere). As mesmas
 * regras de STRINGS valem aqui: vocabulário fechado (§13.7), variáveis de §13.6.1 e nenhuma frase com lado.
 *
 * O que o replay reusa de STRINGS continua lá e sai por t(): abas de cargo (`tabs.*`), status "Como estava às
 * {HHhMM}" (`status.replay*`), "Não há registro de {HHhMM}." (`notice.noRecord`), o link do TSE
 * (`source.stamp.linkAria`) e os títulos de lugar e de cargo (`title.*`).
 *
 * Variáveis novas: `{DATA}` (dia e mês por extenso, de fmtDateLong), `{ANO}`, `{TURNO}` (1 ou 2), `{TITULO}` (o título
 * da rota), `{FONTE}`, `{CONFERENCIA}` e `{METODO}` (textos que o motor escreve no manifesto: `provenance.label` e
 * `provenance.method_note`), `{NOME}` e `{SHA}` (arquivo de origem), `{REFERENCIA}`, `{ESCOPO}` e `{COBERTURA}`
 * (conferência), `{ARQUIVO}` (o que faltou carregar), `{VEL}` (velocidade do player), `{DE}` e `{ATE}` (as horas das
 * pontas da barra de tempo) e `{DIA}` (a data curta COM ANO do instante mostrado, de fmtNightDate: "30/10/2022").
 *
 * Duas chaves, e só elas, citam uma candidatura (`{X}`, com a concordância `{eleito:X}`): a manchete e o anúncio da
 * decisão NA PÁGINA DO REPLAY (FB-03 revisão, ajuste 6), no passado e com o ano da noite. O mesmo modelo vale para os
 * dois lados: trocar 13 e 22 nos dados troca só o nome (tests/unit/replay/copy.test.ts).
 */
export const REPLAY_STRINGS = {
  // ReplayBanner: faixa fixa, obrigatória em toda tela do replay, sem como fechar (§9 do desenho, item 29 do léxico)
  'replay.banner.title': 'REPLAY DE ELEIÇÃO ANTERIOR — apuração de {DATA} de {ANO}',
  'replay.banner.title.noNight': 'REPLAY DE ELEIÇÃO ANTERIOR',
  // no hub, sem noite escolhida: a 1ª linha é só esta
  'replay.banner.title.hub': 'REPLAY DE ELEIÇÕES ANTERIORES',
  'replay.banner.disclaimer': 'Não é previsão e não tem relação com apurações futuras.',
  'replay.banner.testData': 'dados de teste',
  'replay.banner.start': 'Início',
  'replay.banner.final': 'Resultado final',
  // Marca no pé de cada cartão da página do replay (item 37 do léxico; AC-FB02-38)
  'replay.card.mark': 'Replay · {DATA} de {ANO}',
  // Marca no TOPO do cartão do herói, antes da manchete: a manchete não tem ano (FB-02 revisão, ajuste 2)
  'replay.hero.mark': 'REPLAY · {DATA} de {ANO}',
  // junto do placar do herói enquanto o instante mostrado tem menos de 100% das seções (FB-03 revisão, ajuste 7)
  'replay.hero.partial': 'Parcial: reflete a ordem de chegada das seções, não o resultado final.',
  // manchete do herói depois da decisão, no passado e com o ano da noite (FB-03 revisão, ajuste 6)
  'replay.headline.elected': '{X} foi {eleito:X} presidente em {ANO}',
  // Título da aba (costura S-3) e cabeçalho (ReplayTopBar)
  'replay.title.night': 'Replay · {DATA} de {ANO} · {TITULO}',
  'replay.title.hub': 'Replay de apurações anteriores · {PRODUCT}',
  'replay.topbar.subtitle': 'Replay · {TURNO}º turno · {DATA} de {ANO}',
  'replay.topbar.subtitle.hub': 'Replay de apurações anteriores',
  'replay.topbar.nights': 'Outras noites',
  'replay.topbar.nights.all': 'Todas as noites',
  'replay.topbar.current': 'Site do {PRODUCT}',
  // ReplaySourceStamp: selo de proveniência. "Fonte: TSE" nunca some ("site não oficial" saiu: ORD-F-145).
  'replay.stamp.reconstructed': 'Fonte: TSE, dados abertos · reconstruído pela hora da 1ª totalização de cada seção',
  'replay.stamp.published': 'Fonte: TSE · como foi publicado na noite',
  'replay.stamp.testData': 'Dados de teste',
  // as duas partes do rótulo do acervo (provenance.label), com o texto do motor
  'replay.stamp.label.source': 'Origem dos dados: {FONTE}',
  'replay.stamp.label.verification': 'Conferência: {CONFERENCIA}',
  // <details> "Método e arquivos"
  'replay.stamp.details': 'Método e arquivos',
  'replay.stamp.method': 'Método: {METODO}',
  'replay.stamp.sources.title': 'Arquivos de origem',
  'replay.stamp.source.retained': '{NOME} · sha256 {SHA}',
  'replay.stamp.source.derived': '{NOME} · derivado, fonte não retida',
  'replay.stamp.checks.title': 'Conferências',
  'replay.stamp.check':
    '{REFERENCIA}: {N} {N|ponto conferido, igual|pontos conferidos, todos iguais} · {ESCOPO} · {COBERTURA}',
  'replay.stamp.check.external': 'Referência externa',
  'replay.stamp.check.internal': 'Conferência interna',
  'replay.stamp.check.scope.BR': 'Brasil',
  'replay.stamp.check.scope.UF': 'estados',
  'replay.stamp.check.scope.MUN': 'municípios',
  'replay.stamp.check.covers.frames': 'quadro a quadro',
  'replay.stamp.check.covers.final': 'no quadro final',
  'replay.stamp.tail':
    'O quadro final soma {N} {N|seção totalizada|seções totalizadas} depois das 06h00 do dia seguinte.',
  'replay.stamp.officialEnd': 'Fim da totalização no TSE: {HHhMM}.',
  'replay.stamp.unmapped': '{N} {N|unidade da noite|unidades da noite} sem posição no mapa de 2026.',
  'replay.stamp.methodology': 'Metodologia do {PRODUCT}',
  // Quadro final fora da linha do tempo (provenance.outside_timeline): sem relógio
  'replay.outside.status': 'Resultado final · fora da linha do tempo',
  'replay.outside.note':
    'Este quadro não tem hora: soma {N} {N|seção que o TSE totalizou|seções que o TSE totalizou} fora da linha do tempo da noite.',
  // NightsHub e NightCard: uma entrada por noite, em ordem de data. Nada sobre o resultado.
  'replay.hub.title': 'Apurações anteriores',
  'replay.hub.intro': 'Reveja como os números do TSE chegaram, minuto a minuto, em cada noite de apuração.',
  'replay.hub.loading': 'Carregando as noites…',
  'replay.hub.empty': 'Nenhuma noite no acervo por enquanto.',
  'replay.hub.error': 'Não foi possível carregar a lista de noites.',
  'replay.hub.notFound': 'Esta noite não está no acervo.',
  'replay.hub.card.title': '{TURNO}º turno · {DATA} de {ANO}',
  'replay.hub.card.open': 'Abrir o replay do {TURNO}º turno de {ANO}',
  'replay.hub.card.editions': '{N} {N|edição|edições}',
  'replay.hub.card.reconstructed': 'Reconstruído dos dados abertos do TSE',
  'replay.hub.card.published': 'Como foi publicado na noite',
  // NightGate: carregando, erro, sem suporte
  'replay.night.loading.catalog': 'Carregando o acervo…',
  'replay.night.loading.manifest': 'Abrindo a noite…',
  'replay.night.loading.frame': 'Carregando os números…',
  'replay.night.error': 'Não foi possível abrir esta noite.',
  'replay.night.unsupported.recording': 'Esta noite ainda está sendo gravada. Volte depois do fim da apuração.',
  'replay.night.unsupported.shape': 'Esta noite ainda não pode ser mostrada aqui.',
  'replay.retry': 'Tentar de novo',
  'replay.boot.failed': 'Não foi possível abrir o replay neste navegador.',
  // 404 do site do replay (replay-404.html, texto estático; tests/unit/replay/page.test.ts confere com estas chaves)
  'replay.notFound.title': 'Página não encontrada',
  'replay.notFound.text': 'Este endereço não existe no replay do {PRODUCT}.',
  // NightNotice: o que faltou carregar (um arquivo ruim nunca derruba o que já está certo na tela)
  'replay.notice.file': 'Não foi possível carregar {ARQUIVO}.',
  'replay.notice.file.catalog': 'a lista de noites',
  'replay.notice.file.manifest': 'o índice da noite',
  'replay.notice.file.chunk': 'os números deste horário',
  'replay.notice.file.municipal': 'os números por município',
  'replay.notice.file.events': 'a lista de acontecimentos',
  'replay.notice.file.insights': 'os quadros de situação incomum',
  'replay.notice.file.analysis': 'a tabela da noite',
  'replay.notice.file.sections': 'as seções',
  'replay.notice.file.commentary': 'os comentários da noite',
  // lentes Situações e Dia de votação (N-20, N-24)
  'replay.analysis.loading': 'Carregando os quadros desta noite…',
  'replay.analysis.error': 'Não foi possível carregar esta aba agora. Tente de novo daqui a pouco.',
  'replay.votingDay.pick': 'Escolha um município no mapa ou na busca para ver o dia de votação das seções dele.',
  'replay.votingDay.none': 'O acervo desta noite não traz as seções deste município.',
  'replay.notice.municipal': 'O mapa por município não está disponível agora. Os números por estado seguem na tela.',
  'replay.notice.events': 'A lista de acontecimentos não está disponível agora. Os números seguem na tela.',
  // Anúncios da LiveRegion: só o que o usuário pediu (instante novo, erro, nova tentativa)
  'replay.announce.instant': 'Mostrando {HHhMM}.',
  'replay.announce.outside': 'Mostrando o resultado final, fora da linha do tempo.',
  'replay.announce.retry': 'Tentando de novo.',
  // com o player (FB-03): o instante e quanto da apuração já tinha chegado, ao pausar, soltar a barra e ir a uma hora
  'replay.announce.shown': 'Mostrando {HHhMM}, {S} das seções.',
  // tocando, quando o relógio cruza a decisão: no passado e com o ano da noite (FB-03 revisão, ajuste 6)
  // ORD-F-152: alinhado à manchete aprovada (§8 de 20/10), no passado e com o ano; o nome aparece duas vezes
  'replay.announce.decision':
    'Em {ANO}, a vantagem de {X} não podia mais ser alcançada; {X} foi {eleito:X} presidente, segundo o TSE.',
  // ReplayDock (FB-03): o player da noite. {VEL} é a velocidade ("0,5", "60"); {DE} e {ATE}, as horas da barra.
  'replay.dock.aria': 'Controles do replay',
  'replay.dock.play': 'Tocar',
  'replay.dock.pause': 'Pausar',
  'replay.dock.restart': 'Rever do início',
  'replay.dock.waiting': 'Carregando…',
  // relógio do dock: a data curta com ano e a hora do quadro que está na tela ("30/10/2022 · 19h12")
  'replay.dock.clock': '{DIA} · {HHhMM}',
  // trecho da noite sem quadros: o relógio vai direto à hora do próximo (nada é interpolado)
  'replay.dock.gap': 'Sem totalização nova até {HHhMM}',
  'replay.dock.slider': 'Hora da noite',
  'replay.dock.valuetext': '{HHhMM}, {S} das seções',
  'replay.dock.valuetext.final': 'Resultado final, fora da linha do tempo',
  'replay.dock.speed': 'Velocidade',
  'replay.dock.speed.current': 'Velocidade: {VEL}×',
  'replay.dock.speed.option': '{VEL}×',
  'replay.dock.goto': 'Ir para a hora',
  'replay.dock.goto.hint': 'hh:mm',
  'replay.dock.goto.submit': 'Ir',
  'replay.dock.goto.invalid': 'Hora fora desta noite. Escolha de {DE} a {ATE}.',
  // marcas da barra: acontecimentos que estão nos eventos da noite, sem nome de candidatura
  'replay.dock.mark.lead': 'Ir para {HHhMM}: troca na primeira posição',
  'replay.dock.mark.decision': 'Ir para {HHhMM}: a vantagem não podia mais ser alcançada',
  // FB-04 (marco 3): lugar, lentes, municípios. Trilha de lugar e "voltar" (cada nível é um link).
  'replay.trail.aria': 'Lugar em foco',
  'replay.nav.aria': 'Lugar, busca e abas',
  'replay.back': 'Voltar',
  'replay.back.aria': 'Voltar para {LUGAR}',
  // Lentes (docs/_trabalho/replay-navegacao-analitica.md §3): os rótulos das abas; a lista é de replay/lenses.ts
  'replay.lens.aria': 'O que ver deste lugar',
  'replay.lens.apuracao': 'Apuração',
  'replay.lens.turnoAnterior': 'Turno anterior',
  'replay.lens.quebras': 'Quebras de padrão',
  'replay.lens.diaDeVotacao': 'Dia de votação',
  'replay.lens.eleitorado': 'Eleitorado',
  'replay.lens.esperado': 'Observado × esperado',
  'replay.lens.situacoes': 'Situações incomuns',
  // o motivo vale para TODA aba desabilitada: não diz que o acervo não traz o dado (o de 2022-2t já traz o par com o
  // 1º turno; quem ainda não mostra é o site)
  'replay.lens.reason.night': 'Esta aba ainda não está disponível nesta noite.',
  'replay.lens.asked':
    'A aba “{LENTE}”, pedida no endereço, ainda não está disponível nesta noite. Mostrando a apuração.',
  'replay.lens.unknown': 'A aba pedida no endereço não existe. Mostrando a apuração.',
  // Busca por município (combobox)
  'replay.search.label': 'Buscar município',
  'replay.search.hint': 'Nome do município ou UF',
  'replay.search.empty': 'Nenhum município com esse nome.',
  'replay.search.loading': 'Carregando a lista de municípios…',
  'replay.search.option': '{NOME} ({UF})',
  'replay.search.more': 'Mostrando {N} de {TOTAL}. Escreva mais para afinar.',
  // anúncio da busca na região viva (depois de uma pausa na digitação)
  'replay.search.count': '{N} {N|município encontrado|municípios encontrados}.',
  // Mapa por município
  'replay.map.mesh.loading': 'Carregando o desenho dos municípios…',
  'replay.map.mesh.error': 'O desenho dos municípios não carregou. As cores por estado seguem no mapa.',
  'replay.map.data.loading': 'Carregando os números por município deste horário…',
  // Lista de municípios da UF: ordem alfabética, nunca por voto
  'replay.mun.list.title': 'Municípios {UF_DE}',
  'replay.mun.list.order': '{N} {N|município|municípios}, em ordem alfabética',
  'replay.mun.list.row': '{NOME}: {P} do eleitorado apurado',
  'replay.mun.list.row.nodata': '{NOME}: sem votos apurados neste horário',
  'replay.mun.col.name': 'Município',
  'replay.mun.col.counted': 'Eleitorado apurado',
  'replay.mun.col.votes': 'Votos de {NOME}',
  // Quadro do município
  'replay.mun.top': 'Replay · {DATA} de {ANO} · {QUANDO}',
  'replay.mun.when': 'como estava às {HHhMM}',
  'replay.mun.when.final': 'resultado final',
  'replay.mun.counted': '{P} do eleitorado apurado',
  'replay.mun.counted.unknown': 'Parcela apurada não informada',
  'replay.mun.scoreboard': 'Presidente em {NOME}',
  'replay.mun.votes': 'Votos',
  'replay.mun.share': 'Dos votos válidos',
  'replay.mun.diff': 'Diferença',
  'replay.mun.diff.none': 'Sem diferença',
  'replay.mun.valid': 'Votos válidos',
  'replay.mun.sections': 'Seções totalizadas',
  'replay.mun.sections.none': 'O acervo desta noite não traz a contagem de seções por município',
  'replay.mun.novotes': 'Sem votos apurados neste horário.',
  'replay.mun.source': 'Fonte: TSE',
  'replay.mun.derived': 'Percentuais e diferença: cálculo de {PRODUCT}, a partir de dados do TSE',
  // sem o nome do produto configurado, a frase não fica com um buraco ("cálculo de , a partir…")
  'replay.mun.derived.site': 'Percentuais e diferença: cálculo deste site, a partir de dados do TSE',
  // Tabela urna a urna (estado "sem dado": o acervo de hoje não traz seções)
  'replay.sections.title': 'Urna a urna',
  'replay.sections.caption': 'Seções de {NOME}, uma linha por urna',
  'replay.sections.col.zone': 'Zona',
  'replay.sections.col.local': 'Local de votação',
  'replay.sections.col.section': 'Seção',
  'replay.sections.col.turnout': 'Comparecimento',
  'replay.sections.empty': 'O acervo desta noite ainda não traz as seções deste município.',
  'replay.sections.loading': 'Carregando as seções deste município…',
  'replay.sections.zoneMissing': 'O acervo não traz a zona {N} deste município: a tabela mostra a zona {M}.',
  'replay.sections.count': '{K} de {N} {N|seção totalizada|seções totalizadas} neste horário',
  'replay.sections.zones': 'O acervo reparte este município em {N} zonas: as seções aparecem uma zona por vez.',
  'replay.sections.deep':
    'O endereço pede {NIVEL}. O acervo desta noite ainda não traz esse nível: os números abaixo são do município inteiro.',
  'replay.sections.level.zone': 'a zona {N}',
  'replay.sections.level.local': 'o local de votação {N}',
  'replay.sections.level.section': 'a seção {N}',
  // FB-06: noites de 1º turno (N candidatos, sempre em ordem de número de urna)
  'replay.fr.board.title': 'Presidente · {LUGAR} · 1º turno de {ANO}',
  'replay.fr.board.title.governor': 'Governador · {LUGAR} · 1º turno de {ANO}',
  'replay.fr.gov.title': 'Governadores · 1º turno de {ANO}',
  'replay.fr.gov.order': '{N} {N|estado|estados}, em ordem alfabética da sigla',
  'replay.fr.gov.row.hint': ', ver todas as candidaturas a governador',
  'replay.fr.gov.sections': '{P} das seções',
  'replay.fr.gov.lead': '{R} {NOME} {P}',
  'replay.fr.gov.empty': 'O arquivo desta noite ainda não traz a disputa de governador.',
  'replay.fr.gov.none': 'Esta página não tem a disputa de governador {UF_EM} nesta noite.',
  'replay.fr.legend.governors': 'Mais votado para governador neste horário, por estado',
  'replay.fr.map.aria.governor':
    'Mapa do Brasil: a cor de cada estado é a do partido do candidato a governador mais votado nele neste horário',
  'replay.fr.marker.aria.governor':
    '{UF}: mais votos para {NOME} na disputa de governador neste horário, com {P} dos votos válidos; {S} das seções totalizadas',
  'replay.fr.board.order': '{N} {N|candidato|candidatos}, em ordem de número de urna',
  'replay.fr.board.list': 'Votos por candidato em {LUGAR}, em ordem de número de urna',
  'replay.fr.board.waiting': 'Carregando os números deste horário…',
  'replay.fr.rank.1': '1º',
  'replay.fr.rank.2': '2º',
  'replay.fr.rank.1.sr': '1º lugar neste horário',
  'replay.fr.rank.2.sr': '2º lugar neste horário',
  'replay.fr.total.sections.value': '{P} ({N} de {T})',
  'replay.fr.total.abstention': 'Abstenção',
  'replay.fr.total.blank': 'Brancos',
  'replay.fr.total.null': 'Nulos',
  'replay.fr.total.ofTurnout': '{N} ({P})',
  'replay.fr.total.note':
    'Candidatos: percentual sobre os votos válidos. Válidos, brancos e nulos: percentual sobre o comparecimento.',
  'replay.fr.derived': 'Percentuais: cálculo de {PRODUCT}, a partir de dados do TSE',
  'replay.fr.derived.site': 'Percentuais: cálculo deste site, a partir de dados do TSE',
  'replay.fr.legend.aria':
    'Legenda do mapa: a cor é a do partido do candidato mais votado em cada lugar, neste horário',
  'replay.fr.legend.states': 'Mais votado neste horário, por estado',
  'replay.fr.legend.municipalities': 'Mais votado neste horário, por município',
  'replay.fr.legend.cities': 'Mais votado neste horário, por cidade',
  'replay.fr.legend.cities.none': 'Os votos por cidade do Exterior ainda não aparecem neste mapa.',
  'replay.fr.map.aria.br': 'Mapa do Brasil: a cor de cada lugar é a do partido do candidato mais votado neste horário',
  'replay.fr.map.aria.uf': 'Mapa {UF_DE}: a cor de cada lugar é a do partido do candidato mais votado neste horário',
  'replay.fr.map.aria.world':
    'Mapa do mundo com as cidades onde brasileiros votam: a cor é a do partido do candidato mais votado neste horário',
  'replay.fr.legend.tie': 'Empate',
  'replay.fr.legend.unknown': 'Mais votado não definido pelo arquivo',
  'replay.fr.legend.none': 'Sem votos',
  'replay.fr.legend.waiting': 'Aguardando os primeiros votos',
  'replay.fr.marker.aria':
    '{UF}: mais votos para {NOME} neste horário, {P} dos votos válidos; {S} das seções totalizadas',
  'replay.fr.marker.aria.tie': '{UF}: empate no maior número de votos; {S} das seções totalizadas',
  'replay.fr.marker.aria.none': '{UF}: sem votos apurados neste horário',
  'replay.fr.mun.col.others': 'Votos dos demais candidatos, somados',
  'replay.fr.mun.col.others.short': 'Demais',
  'replay.fr.mun.others': 'Demais candidatos, somados',
  'replay.fr.mun.others.value': '{N} ({P} dos votos válidos)',
  'replay.fr.mun.note':
    'Por município, o acervo desta noite só traz os votos dos dois candidatos que foram ao 2º turno e o total de votos válidos; os demais candidatos aparecem somados. É um limite do acervo, não um destaque deste site.',
  'replay.fr.mun.undefined':
    'Neste horário os números deste município não fecham no acervo (votos válidos abaixo da soma dos dois candidatos). Mais votado não definido.',
  // Cadastro da noite: o manifesto não trouxe o nome de um número
  'replay.candidate.unnamed': 'Candidatura {N}',
  // Páginas de compartilhamento por noite (/{noite}/; vitrine do replay, §8 de 16/10, 12h20, item 2 d): o título e a
  // descrição do og:* e o link para quem chega sem o redirecionamento. Sem nome de candidato (neutralidade).
  'replay.share.2022_1t.title': 'Reveja a apuração do 1º turno de 2022',
  'replay.share.2022_1t.description':
    'A noite de 2 de outubro de 2022, minuto a minuto, com os dados abertos do TSE. Não é previsão.',
  'replay.share.2022_2t.title': 'Reveja a apuração do 2º turno de 2022',
  'replay.share.2022_2t.description':
    'A noite de 30 de outubro de 2022, minuto a minuto, com os dados abertos do TSE. Não é previsão.',
  'replay.share.2026_1t.title': 'Reveja a apuração do 1º turno de 2026',
  'replay.share.2026_1t.description':
    'A noite de 4 de outubro de 2026, minuto a minuto, com os dados do TSE. Não é previsão.',
  'replay.share.open': 'Abrir o replay',
  // ReplayBridge: a ponte para o dia 25 (item 1.13, parte 1; atrás de features.replay_bridge). PARA APROVAR (todas as
  // chaves replay.bridge.*). O texto principal é o da ordem do item 1.13; {SITE} é o endereço do site ao vivo (o mesmo
  // do link "Site do {PRODUCT}"), dito sem "https://" e posto como link.
  'replay.bridge.text': 'A apuração do 2º turno de 2026 é no domingo, 25/10, às 17h00, em {SITE}',
  // o lembrete: os MESMOS textos de home.agenda.* (copy/home.ts, página inicial do site ao vivo), copiados aqui para o
  // catálogo da página inicial não entrar no JS crítico do replay (OR-01); tests/unit/replay/bridge.test.tsx confere
  // que seguem iguais
  'replay.bridge.ics': 'Baixar o lembrete (.ics)',
  'replay.bridge.google': 'Adicionar ao Google Agenda',
  'replay.bridge.google.label': 'Adicionar ao Google Agenda (abre o site do Google em nova aba)',
  'replay.bridge.event': 'Apuração do 2º turno de 2026 no {PRODUCT}',
  'replay.bridge.details':
    'Acompanhe a apuração do 2º turno, município por município, com os números do TSE: https://mapa.voto/',
  // a ponte no pé do cartão do município (item 1.13, parte 2): o texto da ordem, aprovado na versão 3; {CIDADE} é o nome
  // do município como o título do cartão o mostra. Única frase do replay com "ao vivo" (fala do site de 25/10, não da
  // noite gravada); tests/unit/replay/copy.test.ts a deixa como exceção do AC-FB02-26
  'replay.bridge.city': 'Acompanhe {CIDADE} ao vivo em 25/10',
} as const satisfies Record<string, string>;

export type ReplayCopyKey = keyof typeof REPLAY_STRINGS;

/** t() do catálogo da página do replay: mesma interpolação, outra lista de chaves. */
export function tReplay(key: ReplayCopyKey, vars: CopyVars = {}): string {
  return (REPLAY_STRINGS[key] as string).replace(TOKEN, (_, body: string) => resolve(key, body, vars));
}
